import React, { useEffect, useMemo, useRef } from 'react'

import {
  useIsCameraMoving,
  useIsCameraZooming,
  usePan,
  usePRFile,
  useZoom,
} from '@sketch-hq/sketch-web-renderer'
import { useDevToolsSetting } from '@sketch/devtools'

import { useAnnotationOverlayOrigin } from 'modules/annotations/hooks'

import { OriginCross, Wrapper } from './ArtboardOriginContent.styles'

export const ArtboardOriginContent: React.FC = ({ children }) => {
  const ref = useRef(null)

  const pan = usePan() || { x: 0, y: 0 }
  const zoom = useZoom() || 1
  const isCameraMoving = useIsCameraMoving()
  const isCameraZooming = useIsCameraZooming()
  const prFile = usePRFile()

  const [showPageOrigin] = useDevToolsSetting('webRenderer.showPageOrigin')

  const [, setAnnotationOverlayOrigin] = useAnnotationOverlayOrigin()

  useEffect(() => {
    setAnnotationOverlayOrigin(ref, zoom)
  }, [setAnnotationOverlayOrigin, zoom])

  const bounds = useMemo(() => {
    const bounds = prFile?.getRootNode()?.getAbsoluteExtent() ?? {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    }
    // Hard code root node bounds to (0,0) whatever. This is because root node
    // bounds of a fragment are still in the page coordinate space, but we want
    // them to be at (0,0) for the Artboard Detail view to position things correctly
    bounds.x = 0
    bounds.y = 0
    return bounds
  }, [prFile])

  const style = {
    willChange: isCameraMoving ? 'contents' : undefined,
    pointerEvents: isCameraMoving ? 'none' : 'auto',
    transform: `translate3d(${Math.round(pan.x * zoom)}px, ${Math.round(
      pan.y * zoom
    )}px, 0)`,
    width: `${bounds.width * zoom}px`,
    height: `${bounds.height * zoom}px`,
    marginTop: `${-bounds.y * zoom}px`,
    marginLeft: `${-bounds.x * zoom}px`,
  } as const

  return (
    <Wrapper
      data-testid="artboard-origin-content"
      ref={ref}
      style={style}
      data-canvas-zooming={isCameraZooming}
    >
      {children}
      {showPageOrigin && <OriginCross />}
    </Wrapper>
  )
}
